import { Button, Spin, Table } from 'antd';
import { ColumnsType, SorterResult, TablePaginationConfig, TableRowSelection } from 'antd/lib/table/interface';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { batch } from 'react-redux';
import {
	ScrollType,
	buildColumn,
	getDefaultPaginationSettings,
	getDefaultTablePaginationConfig,
	handleResponse,
	invertSelectedRowKeys,
	useHandleOnChangeTableLogic,
} from '../../../../../../Helper';
import { deviceAdminApi } from '../../../../../../api';
import { MemberColumn, MemberPaginationSettings } from '../../../../../../model/CommonModel';
import { MembersModel } from '../../../../../../model/DeviceAdminModel';
import { Modal, SearchColumn } from '../../../../..//common/';
import { RoleStore } from '../../RoleContext/context';
import { ExpandableContent } from '../ExpandableContent/ExpandableContent';
import styles from './addMembersModal.module.scss';

type Props = {
	onClose: () => void;
	onConfirm: (selectedRowKeys: React.Key[]) => void;
	nonSubscribedOperatorsIds: number[];
};

//Avoid creating object style inline, since increases reconciliations
const scroll: ScrollType = { x: 379, y: 325 };

const AddMembersModal: React.FC<Props> = ({ onClose, onConfirm, nonSubscribedOperatorsIds }) => {
	//#region state management variables
	const {
		roleContextInitialState: {
			props: { roleId, roleName },
			member: { subscribedOperatorIds },
		},
	} = useContext(RoleStore);

	const [isTableLoading, setIsTableLoading] = useState<boolean>(true);
	const [tableData, setTableData] = useState<MemberColumn[] | null>(null);
	const [tablePaginationSetting, setTablePaginationSetting] = useState<MemberPaginationSettings>({
		...getDefaultPaginationSettings(),
		RoleId: roleId,
		IncludedInList: false,
	});
	const [shouldResetSearchColumn, setShouldResetSearchColumn] = useState<boolean>(false);
	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

	const isSearchPerformed = useRef<boolean>(false);
	//#endregion

	const handleOnChangeTableLogic = useHandleOnChangeTableLogic();

	useEffect(() => {
		if (roleId) {
			loadMembers(subscribedOperatorIds, { ...tablePaginationSetting, RoleId: roleId });
		}
	}, [roleId]);

	const loadMembers = async (membersIds: number[], paginationSetting: MemberPaginationSettings): Promise<void> => {
		if (!isTableLoading) {
			setIsTableLoading(true);
		}

		batch(() => {
			deviceAdminApi
				.getMembers(membersIds, paginationSetting)
				.then(response => {
					if (!handleResponse(response)) {
						const { Members, TotalItems } = response.Entity;
						const membersMapped = mapMembers(Members);
						setTableData(membersMapped);
						setTablePaginationSetting({ ...paginationSetting, TotalItems: TotalItems });
					}
				})
				.finally(() => {
					setIsTableLoading(false);
				});
		});
	};

	const mapMembers = (data: MembersModel[]): MemberColumn[] => {
		if (data.length > 0) {
			return data.map<MemberColumn>((d: MembersModel, index: number) => ({
				...d,
				Index: index,
				key: d.OperatorId.toString(),
				Action: undefined,
			}));
		}

		return [];
	};

	const clearSelection = () => {
		setSelectedRowKeys([]);
	};

	const handleChangePagination = (page: number, pageSize: number) => {
		if (pageSize !== tablePaginationSetting.PageSize) {
			clearSelection();
			setTablePaginationSetting({ ...tablePaginationSetting, PageNumber: page, PageSize: pageSize });
		} else {
			setTablePaginationSetting({ ...tablePaginationSetting, PageNumber: page });
		}
	};

	const handleOnChangeTable = (pagination: TablePaginationConfig, filters: { Name?: string[] }, sorter: SorterResult<MemberColumn>) => {
		const { current, pageSize, shouldUpdateSearchResults, shouldUpdateTableResults, sortField, sortOrder } = handleOnChangeTableLogic({
			clearSelection,
			filters,
			handleChangePagination,
			isSearchPerformed,
			pagination,
			shouldResetSearchColumn,
			sorter,
			tablePaginationSetting,
		});

		if (shouldUpdateTableResults || shouldUpdateSearchResults) {
			setIsTableLoading(true);
			loadMembers(subscribedOperatorIds, {
				...tablePaginationSetting,
				PageNumber: current,
				PageSize: pageSize,
				SortDirection: sortOrder,
				SortField: sortField.toString(),
			});
		}
	};

	const pagination: TablePaginationConfig = getDefaultTablePaginationConfig(
		false,
		handleChangePagination,
		tablePaginationSetting.PageNumber,
		tablePaginationSetting.PageSize,
		tablePaginationSetting.TotalItems,
		undefined,
		selectedRowKeys
	);

	const setSearchPerformedSource = (value: boolean) => {
		isSearchPerformed.current = value;
	};

	const handleOnSearch = (searchString: string) => {
		loadMembers(subscribedOperatorIds, { ...tablePaginationSetting, SearchedValue: searchString });
	};

	const handleSelectAll = () => {
		setSelectedRowKeys(nonSubscribedOperatorsIds.map<React.Key>(x => x.toString()));
	};

	const handleSelectInverse = () => {
		const dataKeys = tableData.map<React.Key>(x => x.key.toString());
		const newSelectedRowKeys = invertSelectedRowKeys(dataKeys, selectedRowKeys);
		setSelectedRowKeys(newSelectedRowKeys);
	};

	const rowSelectionMembers: TableRowSelection<MemberColumn> = {
		preserveSelectedRowKeys: true,
		type: 'checkbox',
		selections: [
			{ key: 'SELECTION_ALL_SOURCE', text: 'Select all data', onSelect: handleSelectAll },
			{ key: 'SELECTION_INVERT_SOURCE', text: 'Invert current page', onSelect: handleSelectInverse },
			{ key: 'SELECTION_NONE', text: 'Clear all data', onSelect: clearSelection },
		],
		onChange: (selectedRowKeys: React.Key[]) => {
			setSelectedRowKeys(selectedRowKeys);
		},
		getCheckboxProps: record => ({
			id: `rolesMembersInTableCheckbox-${record.key}`,
			disabled: false,
			children: <label htmlFor={`rolesAddMembersInTableCheckbox-${record.key}`} className={styles.srOnly} />,
		}),
		columnWidth: 50,
		selectedRowKeys: selectedRowKeys,
	};

	const generateColumns = () => {
		const columns: ColumnsType<MemberColumn> = [
			{
				...buildColumn(_('Name'), 'Name', '200px', 'start'),
				key: 'AddMembersRole-Column-1',
				sorter: (a, b) => a.Name.localeCompare(b.Name),
				...SearchColumn({
					maxLength: 20,
					dataIndex: 'Name',
					reset: undefined,
					label: undefined,
					notVisible: undefined,
					resetSearch: shouldResetSearchColumn,
					clearSelection: clearSelection,
					handleResetSearch: () => setShouldResetSearchColumn(false),
					setSearchResults: (searchedValue: string) => handleOnSearch(searchedValue),
					setSearchedValue: undefined,
					searchColumnId: undefined,
					setSearchPerformed: (value: boolean) => setSearchPerformedSource(value),
				}),
			},
			{
				...buildColumn(_('Domain'), 'DirectorySource', '200px', 'start'),
				key: 'AddMembersRole-Column-2',
			},
			{
				...buildColumn(_('MemberOf'), 'BelongsToGroups', 'auto', 'start'),
				key: 'AddMembersRole-Column-3',
				render: (text: string) => {
					return <ExpandableContent text={text} />;
				},
			},
		];

		return columns;
	};

	const internalOnConfirm = () => onConfirm(selectedRowKeys);

	const title = `${_('AddNewMemberTo')} ${roleName}`;

	return (
		<Modal
			visible
			title={title}
			footer={[
				<Button id='addMemberSaveButton' key='save' type='primary' onClick={internalOnConfirm} disabled={selectedRowKeys?.length === 0}>
					{_('ApplySelection')}
				</Button>,
				<Button id='AddMemberCancelButton' key='cancel' onClick={onClose}>
					{_('Cancel')}
				</Button>,
			]}
			onClickOk={internalOnConfirm}
			onCancel={onClose}
			width='900px'
			className={styles.WithModalZoom}>
			<Spin wrapperClassName={styles.spin} tip={`${_('Loading')}...`} spinning={isTableLoading} size='large'>
				<div>
					<Table
						size='small'
						dataSource={tableData}
						columns={generateColumns()}
						rowSelection={{ ...rowSelectionMembers }}
						pagination={pagination}
						scroll={scroll}
						onChange={handleOnChangeTable}
						className={styles.tableStyle}
					/>
				</div>
			</Spin>
		</Modal>
	);
};

export { AddMembersModal };
